import { useCallback, useMemo, useState } from 'react';
import type { DatasetLookup, DatasetLookupCandidate } from 'src/components/star-grid/datasets/datasetTypes';
import { candidateChecks, type LookupChecks } from 'src/components/star-grid/datasets/utils/lookupChecks';

/** The wizard's accepted lookups and their checks; a removed suggestion stays dismissed until re-added. */
export function useLookupSelections() {
  const [lookups, setLookups] = useState<DatasetLookup[]>([]);
  const [checks, setChecks] = useState<Record<string, LookupChecks>>({});
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());

  const seed = useCallback((initial: readonly DatasetLookup[]) => {
    setLookups([...initial]);
    setChecks({});
    setDismissed(new Set());
  }, []);

  const upsert = useCallback((lookup: DatasetLookup, lookupChecks: LookupChecks) => {
    setLookups((prev) => [...prev.filter((existing) => existing.column !== lookup.column), lookup]);
    setChecks((prev) => ({ ...prev, [lookup.column]: lookupChecks }));
    setDismissed((prev) => new Set([...prev].filter((column) => column !== lookup.column)));
  }, []);

  const accept = useCallback(
    (candidate: DatasetLookupCandidate) =>
      upsert(
        { column: candidate.column, table_ref: candidate.table_ref, key: candidate.key, attributes: [...candidate.attributes] },
        candidateChecks(candidate)
      ),
    [upsert]
  );

  const remove = useCallback((column: string) => {
    setLookups((prev) => prev.filter((lookup) => lookup.column !== column));
    setChecks((prev) => Object.fromEntries(Object.entries(prev).filter(([existing]) => existing !== column)));
    setDismissed((prev) => new Set(prev).add(column));
  }, []);

  const pending = useCallback(
    (candidates: readonly DatasetLookupCandidate[]) =>
      candidates.filter((candidate) => !lookups.some((lookup) => lookup.column === candidate.column) && !dismissed.has(candidate.column)),
    [lookups, dismissed]
  );

  return useMemo(() => ({ lookups, checks, seed, upsert, accept, remove, pending }), [lookups, checks, seed, upsert, accept, remove, pending]);
}
